import { ReactNode, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineSelect,
  InlineStatus,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Department = { id: number; code: string; name: string; is_active: boolean }

type JobProfile = {
  id: number
  code: string
  name: string
  description: string
  purpose: string
  responsibilities: string
  required_qualifications: string
  kpi_description: string
  competencies: string
  document_url: string
  default_department: number | null
  default_department_name: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

const STATUS_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-700',
}

type TabKey = 'overview' | 'files'

export default function HRJobProfileDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const profileId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const profileQ = useQuery({
    queryKey: ['hr', 'job-profile', profileId],
    queryFn: async () => (await api.get<JobProfile>(`/hr/job-profiles/${profileId}/`)).data,
    enabled: !!profileId,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<JobProfile>) =>
      (await api.patch(`/hr/job-profiles/${profileId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'job-profile', profileId] })
      qc.invalidateQueries({ queryKey: ['hr', 'job-profiles'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/hr/job-profiles/${profileId}/`),
    onSuccess: () => {
      toast.success('Job profile deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'job-profiles'] })
      navigate('/admin/hr/job-profiles')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('overview')

  if (profileQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (profileQ.isError || !profileQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Job profile not found.</p>
          <Link to="/admin/hr/job-profiles" className="text-blue-600 hover:underline">← Back to job profiles</Link>
        </div>
      </AdminLayout>
    )
  }

  const profile = profileQ.data
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const save = (payload: Partial<JobProfile>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  const statusKey: 'ACTIVE' | 'INACTIVE' = profile.is_active ? 'ACTIVE' : 'INACTIVE'

  return (
    <AdminLayout>
      {deleteDialog}

      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/hr/job-profiles" className="text-muted-foreground hover:text-foreground hover:underline">← Job profiles</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{profile.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={profile.name} onSave={(v) => save({ name: v })} />
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={statusKey}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`job profile ${profile.code}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete job profile</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['overview', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'overview' && 'Overview'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
      <div className="space-y-6 max-w-3xl">

        {/* ── Identity ── */}
        <div className="bg-card rounded-lg shadow p-6">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">Identity</h3>
          <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
            <Row label="Code">
              <InlineText value={profile.code} onSave={(v) => save({ code: v.toUpperCase() })} />
            </Row>
            <Row label="Name">
              <InlineText value={profile.name} onSave={(v) => save({ name: v })} />
            </Row>
            <Row label="Default department">
              <InlineSelect
                value={profile.default_department ? String(profile.default_department) : ''}
                displayLabel={profile.default_department_name || <Dash />}
                allowClear
                options={departments.map((d) => ({
                  value: String(d.id), label: `${d.code} ${d.name}`, searchText: `${d.code} ${d.name}`,
                }))}
                onSave={(v) => save({ default_department: v ? Number(v) : null })}
                helper="Where employments holding this profile typically sit."
              />
            </Row>
            <Row label="Overview">
              <InlineText
                value={profile.description}
                multiline
                onSave={(v) => save({ description: v })}
                placeholder="Brief summary of the role…"
              />
            </Row>
          </dl>
        </div>

        {/* ── Job Profile Description annex ── */}
        <div className="bg-card rounded-lg shadow p-6">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Job Profile Description</h3>
          <p className="text-xs text-muted-foreground mb-4">
            These sections are included in the employment contract as an annex via{' '}
            <span className="font-mono">{'{{ job_profile.* }}'}</span> template variables.
          </p>
          <dl className="grid grid-cols-[200px_1fr] gap-x-6 gap-y-5 text-sm">
            <Row label="Purpose">
              <InlineText
                value={profile.purpose}
                multiline
                onSave={(v) => save({ purpose: v })}
                placeholder="Why this role exists — its mission and contribution…"
              />
            </Row>
            <Row label="Key responsibilities">
              <InlineText
                value={profile.responsibilities}
                multiline
                onSave={(v) => save({ responsibilities: v })}
                placeholder="One responsibility per line…"
              />
            </Row>
            <Row label="Required qualifications">
              <InlineText
                value={profile.required_qualifications}
                multiline
                onSave={(v) => save({ required_qualifications: v })}
                placeholder="Education, certifications, experience…"
              />
            </Row>
            <Row label="KPIs / Evaluation">
              <InlineText
                value={profile.kpi_description}
                multiline
                onSave={(v) => save({ kpi_description: v })}
                placeholder="How performance will be evaluated…"
              />
            </Row>
            <Row label="Competencies">
              <InlineText
                value={profile.competencies}
                multiline
                onSave={(v) => save({ competencies: v })}
                placeholder="Behavioural and technical competencies…"
              />
            </Row>
            <Row label="External document URL">
              <InlineText
                value={profile.document_url}
                onSave={(v) => save({ document_url: v })}
                placeholder="https://… (PDF or Word annex hosted externally)"
              />
            </Row>
          </dl>
        </div>

      </div>
      )}
      {tab === 'files' && <FilesTab parent="hr.jobprofile" parentId={profileId} />}
    </AdminLayout>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
